/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  BriefcaseMedical,
  ChevronRight,
  ChevronsUpDown,
  FileBarChart2,
  FileCheck,
  Folder,
  Home,
  Info,
  LogOut,
  PencilRuler,
  Replace,
  Settings,
  Stamp,
  Upload,
} from "lucide-react";

import {
  Avatar,
  AvatarFallback,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  getUserInitials,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useSidebar,
} from "@alaffia-technology-solutions/ds";
import { useFeatureFlagDynamic } from "@alaffia-technology-solutions/features";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import { UserTypeEnum } from "~/gql/graphql";

import { AlaffiaClientContext } from "../../alaffiaClient/alaffiaClientProvider";
import { isServerSideLogoutEnabled } from "../auth/logoutConfig";
import { performLogout } from "../auth/performLogout";
import { useUserContext } from "../context/User.context";
import { AlaffiaLogo } from "../misc/AlaffiaLogo";
import { UploadClaimModal } from "../uploadClaims/uploadClaimModal";
import { NavigationSubItem } from "./navigationSubitem";
import { NavLink } from "./NavLink";
import { VersionInfo } from "./version.ver";

const Permissioned = ({ components, permissions }: any) => {
  return (
    components
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      .filter(([component]) =>
        permissions.navigation.includes(component.displayName),
      )
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      .map(([component, props], index) =>
        React.createElement(component, { ...props, key: index }),
      )
  );
};

const Queues = ({ variant }: { variant?: "sidebar" | "dropdown" }) => (
  <NavigationSubItem
    to="/queues"
    icon={Stamp}
    label="Queues"
    variant={variant}
  />
);
Queues.displayName = "Queues";

const NewQueues = ({ variant }: { variant?: "sidebar" | "dropdown" }) => (
  <NavigationSubItem
    to="/new-queues"
    icon={Stamp}
    label="New Queues"
    variant={variant}
  />
);
NewQueues.displayName = "Queues";

const Reports = ({ variant }: { variant?: "sidebar" | "dropdown" }) => (
  <NavigationSubItem
    to="/reports"
    icon={FileBarChart2}
    label="Reports"
    variant={variant}
  />
);
Reports.displayName = "Reports";

const Transfers = ({ variant }: { variant?: "sidebar" | "dropdown" }) => (
  <NavigationSubItem
    to="/transfers"
    icon={Replace}
    label="Transfers"
    variant={variant}
  />
);
Transfers.displayName = "Transfers";

const CreateAuditOrReport = ({ setCreatingReport }: any) => (
  <SidebarMenuItem>
    <SidebarMenuButton
      onClick={() => setCreatingReport(true)}
      tooltip="Create Report"
    >
      <FileCheck />
      <span>Create Report</span>
    </SidebarMenuButton>
  </SidebarMenuItem>
);
CreateAuditOrReport.displayName = "CreateAuditOrReport";

const AuditsTab = ({ permissions }: any) => {
  const { state, isMobile } = useSidebar();
  const location = useLocation();
  const pathname = location.pathname;

  const childRoutes = ["/queues", "/new-queues", "/reports", "/transfers"];
  const isAnyChildActive = childRoutes.some((route) =>
    pathname?.startsWith(route),
  );
  const [isOpen, setIsOpen] = useState(isAnyChildActive);

  useEffect(() => {
    setIsOpen(isAnyChildActive);
  }, [isAnyChildActive]);

  const subItems = Permissioned({
    components: [[Queues], [NewQueues], [Reports], [Transfers]],
    permissions,
  });
  const hasSubItems = subItems.length > 0;

  if (!hasSubItems) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Audit Tools">
          <Link to="/audits">
            <Folder />
            <span>Audit Tools</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  if (state === "collapsed" && !isMobile) {
    const dropdownItems = [];
    if (permissions.navigation.includes("Queues")) {
      dropdownItems.push(<Queues key="Queues" variant="dropdown" />);
      dropdownItems.push(<NewQueues key="NewQueues" variant="dropdown" />);
    }
    if (permissions.navigation.includes("Reports")) {
      dropdownItems.push(<Reports key="Reports" variant="dropdown" />);
    }
    if (permissions.navigation.includes("Transfers")) {
      dropdownItems.push(<Transfers key="Transfers" variant="dropdown" />);
    }

    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              tooltip="Audit Tools"
              isActive={isAnyChildActive}
              data-testid="audits-menu-button"
            >
              <Folder />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="right"
            align="start"
            sideOffset={8}
            className="af-border-none"
          >
            <DropdownMenuGroup>{dropdownItems}</DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setIsOpen}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip="Audit Tools" isActive={isAnyChildActive}>
            <Folder />
            <span>Audit Tools</span>
            <ChevronRight
              className={cn(
                "af-ml-auto af-transition-transform af-duration-200",
                isOpen && "af-rotate-90",
              )}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>{subItems}</SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
AuditsTab.displayName = "AuditsTab";

const ClaimsTab = () => (
  <NavLink href="/claims" tooltip="Claims">
    <BriefcaseMedical />
    <span>Claims</span>
  </NavLink>
);
ClaimsTab.displayName = "ClaimsTab";

const Audits = () => (
  <NavLink href="/audits" tooltip="Audits">
    <FileCheck />
    <span>Audits</span>
  </NavLink>
);
Audits.displayName = "Audits";

const HomeTab = ({ homeRoute }: { homeRoute: string }) => (
  <NavLink href={homeRoute} tooltip="Home">
    <Home />
    <span>Home</span>
  </NavLink>
);
HomeTab.displayName = "HomeTab";

const AdminSettings = ({ className }: React.ComponentProps<typeof NavLink>) => (
  <NavLink
    href="/settings/users"
    tooltip="Admin Settings"
    className={className}
  >
    <Settings />
    <span>Admin Settings</span>
  </NavLink>
);
AdminSettings.displayName = "AdminSettings";

const AutodorRules = ({ className }: React.ComponentProps<typeof NavLink>) => {
  const { value: isFeatureEnabled } = useFeatureFlagDynamic({
    key: "enable_autodor_rules_ui",
    type: "boolean",
    defaultValue: false,
  });
  const { userType } = useUserContext();
  const isAdmin = userType === UserTypeEnum.ADMINISTRATOR;

  if (!isFeatureEnabled || !isAdmin) {
    return null;
  }

  return (
    <div className="af-mt-4">
      <SidebarGroupLabel>Tools</SidebarGroupLabel>
      <NavLink href="/autodor-rules" tooltip="Rules" className={className}>
        <PencilRuler />
        <span>Rules</span>
      </NavLink>
    </div>
  );
};
AutodorRules.displayName = "AutodorRules";

const UserProfile = () => (
  <NavigationSubItem to="/user/profile" label="Profile" />
);
UserProfile.displayName = "UserProfile";

const UserProfileDropdown = () => (
  <NavigationSubItem to="/user/profile" label="Profile" variant="dropdown" />
);
UserProfileDropdown.displayName = "UserProfile";

const UserLogout = () => {
  const alaffiaClient = useContext(AlaffiaClientContext);
  return (
    <NavigationSubItem
      icon={LogOut}
      label="Sign out"
      onClick={() => {
        if (isServerSideLogoutEnabled() && alaffiaClient) {
          void performLogout(alaffiaClient);
          return;
        }
        alaffiaClient?.auth.logout({});
        window.location.reload();
      }}
    />
  );
};
UserLogout.displayName = "UserLogout";

const UserLogoutDropdown = () => {
  const alaffiaClient = useContext(AlaffiaClientContext);
  return (
    <NavigationSubItem
      icon={LogOut}
      label="Sign out"
      variant="dropdown"
      onClick={() => {
        if (isServerSideLogoutEnabled() && alaffiaClient) {
          void performLogout(alaffiaClient);
          return;
        }
        alaffiaClient?.auth.logout({});
        window.location.reload();
      }}
    />
  );
};
UserLogoutDropdown.displayName = "UserLogout";

interface UserTabProps {
  permissions: any;
  firstName: string;
  lastName: string;
  email: string;
}
const UserTab = ({ permissions, firstName, lastName, email }: UserTabProps) => {
  const { isMobile } = useSidebar();
  const location = useLocation();
  const pathname = location.pathname;

  const childRoutes = ["/user/profile"];
  const isAnyChildActive = childRoutes.some((route) =>
    pathname?.startsWith(route),
  );

  const dropdownItems = [];
  if (permissions.navigation.includes("UserProfile")) {
    dropdownItems.push(<UserProfileDropdown key="UserProfile" />);
  }
  if (permissions.navigation.includes("UserLogout")) {
    dropdownItems.push(<UserLogoutDropdown key="UserLogout" />);
  }

  const hasDropdownItems = dropdownItems.length > 0;

  if (!hasDropdownItems) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          className="group-data-[collapsible=icon]:af-rounded-full"
          asChild
          tooltip="User"
          data-testid="user-menu-button"
        >
          <Link to="/user">
            <Avatar className="af-h-8 af-w-8">
              <AvatarFallback>
                {firstName && lastName
                  ? getUserInitials(firstName, lastName)
                  : ""}
              </AvatarFallback>
            </Avatar>
            <div className="af-flex af-min-w-0 af-flex-1 af-flex-col af-text-left af-text-sm af-leading-tight">
              <span className="af-truncate af-font-semibold">
                {firstName} {lastName}
              </span>
              <span className="af-line-clamp-1 af-overflow-hidden af-break-words af-text-xs">
                {email}
              </span>
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className="data-[state=open]:af-bg-interface-accent data-[state=open]:af-text-interface-accent-foreground group-data-[collapsible=icon]:af-rounded-full"
            tooltip="User"
            isActive={isAnyChildActive}
            data-testid="user-menu-button"
          >
            <Avatar className="af-h-8 af-w-8">
              <AvatarFallback>
                {firstName && lastName
                  ? getUserInitials(firstName, lastName)
                  : ""}
              </AvatarFallback>
            </Avatar>
            <div className="af-grid af-min-w-0 af-flex-1 af-text-left af-text-sm af-leading-tight">
              <span className="af-truncate af-font-semibold">
                {firstName} {lastName}
              </span>
              <span className="af-truncate af-text-xs">{email}</span>
            </div>
            <ChevronsUpDown className="af-ml-auto af-size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="af-w-[--radix-dropdown-menu-trigger-width] af-min-w-56 af-rounded-lg"
          side={isMobile ? "bottom" : "right"}
          align="end"
          sideOffset={8}
        >
          <DropdownMenuLabel className="af-p-0 af-font-normal">
            <div className="af-flex af-items-center af-gap-2 af-px-1 af-py-1.5 af-text-left af-text-sm">
              <Avatar className="af-h-8 af-w-8">
                <AvatarFallback>
                  {firstName && lastName
                    ? getUserInitials(firstName, lastName)
                    : ""}
                </AvatarFallback>
              </Avatar>
              <div className="af-grid af-flex-1 af-text-left af-text-sm af-leading-tight">
                <span className="af-truncate af-font-semibold">
                  {firstName} {lastName}
                </span>
                <span className="af-truncate af-text-xs">{email}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>{dropdownItems}</DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};
UserTab.displayName = "UserTab";

const UploadClaims = ({ setUploadClaimModalOpen, uploadClaimPending }: any) => {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setUploadClaimModalOpen(true)}
        tooltip="Upload Claims"
      >
        <Upload />
        <span>Upload Claims</span>
      </SidebarMenuButton>
      {uploadClaimPending > 0 && (
        <SidebarMenuBadge>{uploadClaimPending}</SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  );
};
UploadClaims.displayName = "UploadClaims";

interface NavigationProps {
  setCreatingReport: (value: boolean) => void;
}
const Navigation = (props: NavigationProps) => {
  const { setCreatingReport } = props;
  const { permissions, claimIntakeEnabled, firstName, lastName, email } =
    useUserContext();
  const [uploadClaimModalOpen, setUploadClaimModalOpen] = useState(false);
  const [uploadClaimPending, setUploadClaimPending] = useState(0);

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarHeader className="af-h-16 af-border-b af-border-interface-border">
          <div className="af-flex af-flex-1 af-items-center af-gap-2">
            <Link to="/">
              <AlaffiaLogo />
            </Link>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup className="af-h-full">
            <SidebarGroupContent className="af-h-full">
              <SidebarMenu className="af-h-full">
                {Permissioned({
                  components: [
                    [HomeTab, { homeRoute: permissions.homeRoute }],
                    [ClaimsTab],
                    [Audits],
                    [AuditsTab, { setCreatingReport, permissions }],
                    ...(claimIntakeEnabled
                      ? [
                          [
                            UploadClaims,
                            {
                              uploadClaimModalOpen,
                              setUploadClaimModalOpen,
                              setUploadClaimPending,
                              uploadClaimPending,
                            },
                          ],
                        ]
                      : []),
                    [AutodorRules],
                    [AdminSettings, { className: "af-mt-auto" }],
                  ],
                  permissions,
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <UserTab
              permissions={permissions}
              firstName={firstName}
              lastName={lastName}
              email={email}
            />
          </SidebarMenu>

          {!VersionInfo.gitTagFull.startsWith("%%") && (
            <div
              className={cn(
                "af-flex af-w-full af-items-center af-justify-center af-py-4 af-text-xs",
                {
                  "af-text-destructive": VersionInfo.gitDirty === "D",
                  "af-text-warning":
                    VersionInfo.gitDirty !== "D" &&
                    VersionInfo.gitTagDistance !== "0",
                  "af-text-muted-foreground":
                    VersionInfo.gitDirty !== "D" &&
                    VersionInfo.gitTagDistance === "0",
                },
              )}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="af-size-4" />
                </TooltipTrigger>
                <TooltipContent>
                  <div>
                    <div>Alaffia App v{VersionInfo.gitTag}</div>
                    {(VersionInfo.gitTagDistance ||
                      VersionInfo.gitDirty === "D") && (
                      <div className="af-text-[0.75em] af-font-light">
                        <div>{VersionInfo.gitTagFull}</div>
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </SidebarFooter>
      </Sidebar>
      <UploadClaimModal
        {...{
          open: uploadClaimModalOpen,
          setOpen: setUploadClaimModalOpen,
          setUploadClaimPending,
        }}
      />
    </>
  );
};
Navigation.displayName = "Navigation";

export {
  Navigation,
  HomeTab,
  Audits,
  AuditsTab,
  ClaimsTab,
  Queues,
  Reports,
  Transfers,
  CreateAuditOrReport,
  AdminSettings,
  AutodorRules,
  UserTab,
  UserProfile,
  UserLogout,
  UploadClaims,
};
